/**
 * Precision-first relevance ranking for free-text search.
 *
 * Tiers (higher score = more relevant):
 *   100 — exact name match (case-insensitive)
 *    80 — name starts with full query
 *    60 — name contains full query as substring
 *    50 — all tokens appear in name
 *    30 — at least one token appears in name
 *    10 — all tokens appear in description / extra fields (none in name)
 *     0 — no relevance (will be filtered out unless `keepZero` true)
 *
 * Tie-breaker: shorter `name` first, then alphabetical.
 */

const norm = (s: unknown): string =>
  typeof s === 'string' ? s.toLowerCase() : '';

const tokenize = (q: string): string[] =>
  q.toLowerCase().trim().split(/\s+/).filter(Boolean);

export function computeRelevance(
  name: string | null | undefined,
  query: string,
  extraText: Array<string | null | undefined> = []
): number {
  const q = query.trim();
  if (!q) return 0;
  const n = norm(name);
  const ql = q.toLowerCase();
  if (!n && extraText.every((t) => !norm(t))) return 0;

  if (n && n === ql) return 100;
  if (n && n.startsWith(ql)) return 80;
  if (n && n.includes(ql)) return 60;

  const tokens = tokenize(q);
  if (tokens.length > 0) {
    const allInName = n && tokens.every((t) => n.includes(t));
    if (allInName) return 50;
    const anyInName = n && tokens.some((t) => n.includes(t));
    if (anyInName) return 30;
    const haystack = extraText.map(norm).join(' ');
    if (haystack && tokens.every((t) => haystack.includes(t))) return 10;
  }
  return 0;
}

export interface RankOptions<T> {
  query: string;
  getName: (row: T) => string | null | undefined;
  /** Optional: additional searchable text fields (description, location, etc). */
  getExtras?: (row: T) => Array<string | null | undefined>;
  /** Keep rows that score 0 (default false — they're filtered out). */
  keepZero?: boolean;
}

/**
 * Sort rows in-place by computed relevance, descending.
 * Tie-breaker: shorter name first, then alphabetical.
 */
export function rankAndSort<T>(rows: T[], opts: RankOptions<T>): T[] {
  const { query, getName, getExtras, keepZero = false } = opts;
  if (!query || !query.trim()) return rows;

  const scored = rows.map((row) => ({
    row,
    score: computeRelevance(getName(row), query, getExtras ? getExtras(row) : []),
  }));

  const filtered = keepZero ? scored : scored.filter((s) => s.score > 0);

  filtered.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const an = (getName(a.row) || '').toLowerCase();
    const bn = (getName(b.row) || '').toLowerCase();
    if (an.length !== bn.length) return an.length - bn.length;
    return an.localeCompare(bn);
  });

  return filtered.map((s) => s.row);
}
